import { useState } from "react";
import { useTranslations } from "next-intl";
import { AnimatePresence, LayoutGroup, motion } from "framer-motion";
import { ArrowDownIcon, ArrowPathIcon } from "@heroicons/react/24/outline";
import { EventShowing, ShowingMessage } from "@types";
import { joinClassNames } from "@utils/helpers";
import { ShowingStatus } from "@utils/enums";
import { useIsShopsite } from "@context/DomainContext";
import ShowingStatusToolTip from "@features/eventListMode/components/showingStatusToolTip";
import { Badge, Button, Card, Text } from "@components/theming";
import { formatShowings } from "../utils/helpers";
import { FireIcon } from "@heroicons/react/24/solid";

interface Props {
  showings: EventShowing[];
  messages: ShowingMessage[];
  handleSelectDate: (id: number | null) => void;
  bookedDateTickets: (id: number) => number | undefined;
}

const item = {
  visible: ({ index }: { index: number }) => ({
    opacity: 1,
    x: 0,
    transition: {
      delay: (index >= 6 ? index - 6 : index) * 0.05 + 0.25,
    },
  }),
  hidden: ({ index, length }: { index: number; length: number }) => ({
    opacity: 0,
    x: 100,
    transition: {
      delay: (length - index) * 0.05,
    },
  }),
};

export default function EventDatePicker({
  showings,
  handleSelectDate,
  bookedDateTickets,
  messages,
}: Props) {
  const [selected, setSelected] = useState<number | null>(null);
  const [seeMore, setSeeMore] = useState(false);

  const t = useTranslations("EventPage");
  const isShopsite = useIsShopsite();

  const allUniquedateShowings = formatShowings(showings, messages, isShopsite);

  const uniquedateShowings =
    allUniquedateShowings.length <= 6 || seeMore
      ? allUniquedateShowings
      : allUniquedateShowings.slice(0, 6);

  return (
    <div
      data-testid="showingdate"
      role="list"
      className={joinClassNames(
        "relative z-10",
        selected || uniquedateShowings.length === 1
          ? "w-full sm:w-fit max-w-full h-full mb-4"
          : "flex flex-col gap-4"
      )}
    >
      <LayoutGroup>
        <motion.div
          layout="position"
          animate={seeMore ? "show" : "hide"}
          variants={{
            show: {
              height: `${
                document.getElementById("see_all_dates")?.clientHeight
              }px`,
            },
            hide: {
              height: `${
                document.getElementById("see_fewer_dates")?.clientHeight
              }px`,
              transition: {
                delay: (allUniquedateShowings.length - 6) * 0.05,
              },
            },
          }}
          className={
            selected || uniquedateShowings.length === 1
              ? "inline"
              : "grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 auto-rows-[46px]"
          }
        >
          <AnimatePresence>
            {uniquedateShowings.map((showing, index) => {
              const bookedTickets = bookedDateTickets(showing.id);

              return (
                <Card
                  asChild
                  key={showing.id}
                  sharedClasses={joinClassNames(
                    selected && selected !== showing.id ? "hidden" : "",
                    showing.isClickable && uniquedateShowings.length > 1
                      ? "cursor-pointer"
                      : !showing.isClickable
                      ? "cursor-not-allowed"
                      : "cursor-default",
                    "w-full h-fit"
                  )}
                  whitelabelClasses={
                    selected === showing.id || uniquedateShowings.length === 1
                      ? "bg-[color:var(--accent-9)] before:bg-[color:var(--gray-2)]"
                      : !showing.isClickable
                      ? "bg-[color:var(--gray-a3)] text-[color:var(--gray-a11)]"
                      : ""
                  }
                  shopsiteClasses={joinClassNames(
                    selected === showing.id || uniquedateShowings.length === 1
                      ? "bg-indigo-50 ring-2 ring-indigo-500"
                      : !showing.isClickable
                      ? "bg-gray-100 text-gray-700"
                      : "bg-white",
                    "px-4 py-2 rounded-md border border-gray-100 shadow transition-colors ease-in-out duration-200 h-full"
                  )}
                >
                  <motion.button
                    initial={
                      uniquedateShowings.length > 1 ? "hidden" : "visible"
                    }
                    animate="visible"
                    exit="hidden"
                    custom={{
                      index: index,
                      length: allUniquedateShowings.length,
                    }}
                    variants={item}
                    onClick={() => {
                      if (!showing.isClickable) return;
                      handleSelectDate(
                        selected === showing.id && uniquedateShowings.length > 1
                          ? null
                          : showing.id
                      );
                      setSelected(
                        selected === showing.id && uniquedateShowings.length > 1
                          ? null
                          : showing.id
                      );
                    }}
                    whileTap={
                      showing.isClickable && uniquedateShowings.length > 1
                        ? {
                            scale: 0.95,
                          }
                        : undefined
                    }
                  >
                    <div className="flex flex-1 justify-between items-center gap-2 text-base font-medium">
                      <div className="flex items-center gap-1 truncate">
                        {showing.status.hot ? (
                          <FireIcon className="h-6 w-6 text-orange-400 shrink-0" />
                        ) : (
                          <></>
                        )}
                        <Text
                          sharedClasses="font-medium truncate"
                          shopsiteClasses={
                            showing.status.id !== ShowingStatus.AVAILABLE
                              ? "text-gray-600"
                              : "text-black"
                          }
                        >
                          {showing.datename}
                        </Text>
                      </div>
                      {selected === showing.id &&
                      uniquedateShowings.length > 1 ? (
                        <div className="flex gap-1 items-center shrink-0">
                          <ArrowPathIcon
                            className={joinClassNames(
                              "h-4 w-4",
                              isShopsite
                                ? "text-gray-500"
                                : "text-[color:var(--gray-a11)]"
                            )}
                          />
                          <Text
                            color="gray"
                            sharedClasses="text-sm"
                            shopsiteClasses="text-gray-500"
                          >
                            {t("calendarmode.changedate")}
                          </Text>
                        </div>
                      ) : showing.isClickable ? (
                        <Text
                          as="p"
                          color="gray"
                          sharedClasses="text-sm shrink-0"
                          shopsiteClasses="text-gray-500"
                        >
                          {showing.shows > 0
                            ? t("calendarmode.numshows", {
                                count: showing.shows,
                              })
                            : ""}
                        </Text>
                      ) : (
                        <div
                          className={joinClassNames(
                            "flex items-center gap-1 p-1 rounded-lg w-fit text-sm shrink-0",
                            showing.status.bgColor ?? ""
                          )}
                        >
                          <span className={showing.status.textColor}>
                            {showing.status.title}
                          </span>
                          <ShowingStatusToolTip
                            title={showing.status.title}
                            description={showing.status.descriptionMsg}
                            statusId={showing.status.id}
                            accessType={showing.access.type}
                          />
                        </div>
                      )}
                    </div>
                    {bookedTickets ? (
                      <Badge
                        sharedClasses="absolute z-10 -right-2 -top-4 !text-xs"
                        shopsiteClasses="!rounded-lg"
                        variant="solid"
                      >
                        {t("amountincart", {
                          amount: bookedTickets,
                        })}
                      </Badge>
                    ) : (
                      <></>
                    )}
                  </motion.button>
                </Card>
              );
            })}
          </AnimatePresence>
        </motion.div>
        {allUniquedateShowings.length > 6 && !selected ? (
          <div className="flex flex-col">
            <AnimatePresence>
              {!seeMore && (
                <Text
                  asChild
                  as="p"
                  color="gray"
                  sharedClasses="text-sm text-center"
                  shopsiteClasses="text-gray-500"
                >
                  <motion.p
                    initial="hide"
                    animate="show"
                    exit="hide"
                    variants={{
                      hide: {
                        opacity: 0,
                        height: 0,
                        marginBottom: 0,
                        transition: {
                          height: { delay: 0.2 },
                          marginBottom: { delay: 0.2 },
                        },
                      },
                      show: {
                        opacity: 1,
                        height: "auto",
                        marginBottom: "8px",
                        transition: {
                          height: { delay: 0.1 },
                          marginBottom: { delay: 0.1 },
                          opacity: { delay: 0.3 },
                        },
                      },
                    }}
                  >
                    {t("calendarmode.nummoredates", {
                      count: allUniquedateShowings.length - 6,
                    })}
                  </motion.p>
                </Text>
              )}
            </AnimatePresence>
            <div className="w-full flex items-center justify-center">
              <Button
                variant="soft"
                shopsiteVariant="soft"
                shopsiteClasses="gap-1 !py-1 !rounded-lg !bg-indigo-50 hover:!bg-indigo-100 hover:!text-indigo-800 !font-normal"
                onClick={() => setSeeMore((prev) => !prev)}
              >
                <ArrowDownIcon
                  className={joinClassNames(
                    "w-4 h-4 transition-transform ease-in-out duration-200",
                    seeMore ? "rotate-180" : ""
                  )}
                />
                <span>
                  {seeMore
                    ? t("calendarmode.seeless")
                    : t("calendarmode.seemore")}
                </span>
              </Button>
            </div>
          </div>
        ) : (
          <></>
        )}
      </LayoutGroup>
      <div
        id="see_fewer_dates"
        className="absolute invisible isolate grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 w-full auto-rows-[46px]"
      >
        {allUniquedateShowings.slice(0, 6).map((showing, index) => {
          const bookedTickets = bookedDateTickets(showing.id);

          return (
            <Card
              asChild
              key={showing.id}
              sharedClasses={joinClassNames(
                showing.isClickable && uniquedateShowings.length > 1
                  ? "cursor-pointer"
                  : !showing.isClickable
                  ? "cursor-not-allowed"
                  : "cursor-default",
                "w-full"
              )}
              whitelabelClasses={
                selected === showing.id || uniquedateShowings.length === 1
                  ? "bg-[color:var(--accent-9)] before:bg-[color:var(--gray-2)]"
                  : !showing.isClickable
                  ? "bg-[color:var(--gray-a3)] text-[color:var(--gray-a11)]"
                  : ""
              }
              shopsiteClasses={joinClassNames(
                selected === showing.id || uniquedateShowings.length === 1
                  ? "bg-indigo-50 ring-2 ring-indigo-500"
                  : !showing.isClickable
                  ? "bg-gray-100 text-gray-700"
                  : "bg-white",
                "px-4 py-2 rounded-md border border-gray-100 shadow transition-colors ease-in-out duration-200 h-full"
              )}
            >
              <motion.button
                initial={uniquedateShowings.length > 1 ? "hidden" : "visible"}
                animate="visible"
                exit="hidden"
                custom={{
                  index: index,
                  length: allUniquedateShowings.length,
                }}
                variants={item}
                onClick={() => {
                  if (!showing.isClickable) return;
                  handleSelectDate(
                    selected === showing.id && uniquedateShowings.length > 1
                      ? null
                      : showing.id
                  );
                  setSelected(
                    selected === showing.id && uniquedateShowings.length > 1
                      ? null
                      : showing.id
                  );
                }}
                whileTap={
                  showing.isClickable && uniquedateShowings.length > 1
                    ? {
                        scale: 0.95,
                      }
                    : undefined
                }
              >
                <div className="flex flex-1 justify-between items-center gap-1 text-base font-medium">
                  <Text
                    sharedClasses="font-medium truncate"
                    shopsiteClasses={
                      showing.status.id !== ShowingStatus.AVAILABLE
                        ? "text-gray-600"
                        : "text-black"
                    }
                  >
                    {showing.datename}
                  </Text>
                  {selected === showing.id && uniquedateShowings.length > 1 ? (
                    <div className="flex gap-1 items-center shrink-0">
                      <ArrowPathIcon
                        className={joinClassNames(
                          "h-4 w-4",
                          isShopsite
                            ? "text-gray-500"
                            : "text-[color:var(--gray-a11)]"
                        )}
                      />
                      <Text
                        color="gray"
                        sharedClasses="text-sm"
                        shopsiteClasses="text-gray-500"
                      >
                        {t("calendarmode.changedate")}
                      </Text>
                    </div>
                  ) : showing.isClickable ? (
                    <Text
                      as="p"
                      color="gray"
                      sharedClasses="text-sm shrink-0"
                      shopsiteClasses="text-gray-500"
                    >
                      {showing.shows > 0
                        ? t("calendarmode.numshows", {
                            count: showing.shows,
                          })
                        : ""}
                    </Text>
                  ) : (
                    <div
                      className={joinClassNames(
                        "flex items-center gap-1 p-1 rounded-lg w-fit text-sm shrink-0",
                        showing.status.bgColor ?? ""
                      )}
                    >
                      <span className={showing.status.textColor}>
                        {showing.status.title}
                      </span>
                      <ShowingStatusToolTip
                        title={showing.status.title}
                        description={showing.status.descriptionMsg}
                        statusId={showing.status.id}
                        accessType={showing.access.type}
                      />
                    </div>
                  )}
                </div>
                {bookedTickets ? (
                  <Badge
                    sharedClasses="absolute z-10 -right-2 -top-4 !text-xs"
                    shopsiteClasses="!rounded-lg"
                    variant="solid"
                  >
                    {t("amountincart", {
                      amount: bookedTickets,
                    })}
                  </Badge>
                ) : (
                  <></>
                )}
              </motion.button>
            </Card>
          );
        })}
      </div>
      <div
        id="see_all_dates"
        className="absolute invisible isolate grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 w-full auto-rows-[46px]"
      >
        {allUniquedateShowings.map((showing, index) => {
          const bookedTickets = bookedDateTickets(showing.id);

          return (
            <Card
              asChild
              key={showing.id}
              sharedClasses={joinClassNames(
                showing.isClickable && uniquedateShowings.length > 1
                  ? "cursor-pointer"
                  : !showing.isClickable
                  ? "cursor-not-allowed"
                  : "cursor-default",
                "w-full"
              )}
              whitelabelClasses={
                selected === showing.id || uniquedateShowings.length === 1
                  ? "bg-[color:var(--accent-9)] before:bg-[color:var(--gray-2)]"
                  : !showing.isClickable
                  ? "bg-[color:var(--gray-a3)] text-[color:var(--gray-a11)]"
                  : ""
              }
              shopsiteClasses={joinClassNames(
                selected === showing.id || uniquedateShowings.length === 1
                  ? "bg-indigo-50 ring-2 ring-indigo-500"
                  : !showing.isClickable
                  ? "bg-gray-100 text-gray-700"
                  : "bg-white",
                "px-4 py-2 rounded-md border border-gray-100 shadow transition-colors ease-in-out duration-200 h-full"
              )}
            >
              <motion.button
                initial={uniquedateShowings.length > 1 ? "hidden" : "visible"}
                animate="visible"
                exit="hidden"
                custom={{
                  index: index,
                  length: allUniquedateShowings.length,
                }}
                variants={item}
                onClick={() => {
                  if (!showing.isClickable) return;
                  handleSelectDate(
                    selected === showing.id && uniquedateShowings.length > 1
                      ? null
                      : showing.id
                  );
                  setSelected(
                    selected === showing.id && uniquedateShowings.length > 1
                      ? null
                      : showing.id
                  );
                }}
                whileTap={
                  showing.isClickable && uniquedateShowings.length > 1
                    ? {
                        scale: 0.95,
                      }
                    : undefined
                }
              >
                <div className="flex flex-1 justify-between items-center gap-1 text-base font-medium">
                  <Text
                    sharedClasses="font-medium truncate"
                    shopsiteClasses={
                      showing.status.id !== ShowingStatus.AVAILABLE
                        ? "text-gray-600"
                        : "text-black"
                    }
                  >
                    {showing.datename}
                  </Text>
                  {selected === showing.id && uniquedateShowings.length > 1 ? (
                    <div className="flex gap-1 items-center shrink-0">
                      <ArrowPathIcon
                        className={joinClassNames(
                          "h-4 w-4",
                          isShopsite
                            ? "text-gray-500"
                            : "text-[color:var(--gray-a11)]"
                        )}
                      />
                      <Text
                        color="gray"
                        sharedClasses="text-sm"
                        shopsiteClasses="text-gray-500"
                      >
                        {t("calendarmode.changedate")}
                      </Text>
                    </div>
                  ) : showing.isClickable ? (
                    <Text
                      as="p"
                      color="gray"
                      sharedClasses="text-sm shrink-0"
                      shopsiteClasses="text-gray-500"
                    >
                      {showing.shows > 0
                        ? t("calendarmode.numshows", {
                            count: showing.shows,
                          })
                        : ""}
                    </Text>
                  ) : (
                    <div
                      className={joinClassNames(
                        "flex items-center gap-1 p-1 rounded-lg w-fit text-sm shrink-0",
                        showing.status.bgColor ?? ""
                      )}
                    >
                      <span className={showing.status.textColor}>
                        {showing.status.title}
                      </span>
                      <ShowingStatusToolTip
                        title={showing.status.title}
                        description={showing.status.descriptionMsg}
                        statusId={showing.status.id}
                        accessType={showing.access.type}
                      />
                    </div>
                  )}
                </div>
                {bookedTickets ? (
                  <Badge
                    sharedClasses="absolute z-10 -right-2 -top-4 !text-xs"
                    shopsiteClasses="!rounded-lg"
                    variant="solid"
                  >
                    {t("amountincart", {
                      amount: bookedTickets,
                    })}
                  </Badge>
                ) : (
                  <></>
                )}
              </motion.button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
